import { Skeleton, Typography, IconButton } from "@mui/material";
import { PageContainer } from "components/PageContainer";
import { MdPendingActions, MdOutlineBrokenImage } from "react-icons/md";
import { FaUser, FaUsers, FaCalendarAlt, FaWarehouse } from "react-icons/fa";
import { ActiveFilters, FilterMapper } from "components/ActiveFilters";

import {
  CardsSide,
  CardsTop,
  Container,
  DashboardContainer,
  DashboardHeader,
  StyledTableContainer,
  TableHeader,
  TablesAndCardsWrapper,
  TablesContainer,
  HeaderBox
} from "./styles";
import { FaCheckCircle, FaClipboardList, FaClock, FaExclamationTriangle, FaTimesCircle } from "react-icons/fa";
import { IoFilter } from "react-icons/io5";
import { DashboardCard } from "./Components/Cards";
import { IDashboard } from "interfaces/IDashboard";
import { useCallback, useEffect, useMemo, useState } from "react";
import { dashboardService } from "services/dashboardService";
import { dateToday } from "utils/dateToday";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { FilterDashboard } from "./Components/FilterDashboard";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "components/MuiButton";
import { SortButton } from "./Components/SortButton";
import { PunctualityTable } from "./Components/PunctualityTable";
import { ComplaintsTable } from "./Components/ComplaintsTable";

export const Dashboard = () => {
  const [data, setData] = useState<IDashboard>({} as IDashboard);
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [params, setParams] = useState(() => {
    const startDateFromUrl = searchParams.get("startDate");
    const endDateFromUrl = searchParams.get("endDate");
    const transshipPointsIdsFromUrl = searchParams.getAll("transshipPointsIds");
    const groupByFromUrl = searchParams.get("groupBy");
    return {
      startDate: startDateFromUrl || dateToday(),
      endDate: endDateFromUrl || dateToday(),
      transshipPointsIds: transshipPointsIdsFromUrl.map(id => parseInt(id)).filter(id => !isNaN(id)) || [],
      groupBy: groupByFromUrl || "driver",
    };
  });

  // Mapper para traduzir parâmetros para português
  const filterMapper: FilterMapper = {
    startDate: {
      label: "Data início",
      icon: <FaCalendarAlt size={14} />,
      formatValue: (value) => {
        try {
          return dayjs(value).format('DD/MM/YYYY');
        } catch {
          return value;
        }
      },
      hidden: params.startDate === params.endDate, // Esconde se for igual à data fim
    },
    endDate: {
      label: "Data fim",
      icon: <FaCalendarAlt size={14} />,
      formatValue: (value) => {
        try {
          return dayjs(value).format('DD/MM/YYYY');
        } catch {
          return value;
        }
      },
      hidden: params.startDate === params.endDate, // Esconde se for igual à data início
    },
    transshipPointsIds: {
      label: "Centros de Distribuição",
      icon: <FaWarehouse size={14} />,
      formatValue: (value) => {
        if (Array.isArray(value)) {
          return value.length === 1 ? '1 CD selecionado' : `${value.length} CDs selecionados`;
        }
        return value;
      },
    },
    groupBy: {
      label: "Visualização",
      icon: params.groupBy === 'driver' ? <FaUser size={14} /> : <FaUsers size={14} />,
      formatValue: (value) => {
        return value === 'driver' ? 'Por Motorista' : 'Por Time';
      },
      hidden: params.groupBy === 'driver', // Esconde se for o valor padrão
    },
  };
  const [sortPunctuality, setSortPunctuality] = useState(true);
  const [sortComplaints, setSortComplaints] = useState(true);
  const [openFilterPopup, setOpenFilterPopup] = useState(false);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      if (!params.startDate) {
        return;
      }
      setLoading(true);
      const response = await dashboardService.getDashboardData(params);
      if (response.errors.length) {
        throw new Error();
      }
      setData(response);
    } catch {
      toast.error("Erro ao buscar dados do dashboard");
    } finally {
      setLoading(false);
    }
  }, [params]);

  const onChangeParams = (newParams: typeof params) => {
    const combinedParams = {
      ...params,
      ...newParams,
    };
    setParams(combinedParams);
    setSearchParams(combinedParams as any);
  };

  const handleRemoveFilter = (filterKey: string) => {
    const newParams = { ...params };

    switch (filterKey) {
      case 'startDate':
      case 'endDate':
        // Volta para hoje quando remove data
        const today = dateToday();
        newParams.startDate = today;
        newParams.endDate = today;
        break;
      case 'transshipPointsIds':
        newParams.transshipPointsIds = [];
        break;
      case 'groupBy':
        newParams.groupBy = 'driver'; // Volta para o padrão
        break;
      default:
        // Typescript safety: apenas se o key existir nos params
        if (filterKey in newParams) {
          delete (newParams as any)[filterKey];
        }
    }

    setParams(newParams);
    setSearchParams(newParams as any);
  };

  const handleClearAllFilters = () => {
    const defaultParams = {
      startDate: dateToday(),
      endDate: dateToday(),
      transshipPointsIds: [],
      groupBy: 'driver',
    };
    setParams(defaultParams);
    setSearchParams(defaultParams as any);
  };

  const sortData = <T,>(data: T[], key: keyof T, ascending: boolean): T[] => {
    return [...data].sort((a, b) => {
      return ascending ? (a[key] as number) - (b[key] as number) : (b[key] as number) - (a[key] as number);
    });
  };

  const sortedPunctualityData = useMemo(() => {
    return data.punctualityData ? sortData(data.punctualityData, "position", sortPunctuality) : [];
  }, [data.punctualityData, sortPunctuality]);

  const sortedComplaints = useMemo(() => {
    return data.complaintsData ? sortData(data.complaintsData, "position", sortComplaints) : [];
  }, [data.complaintsData, sortComplaints]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <Container>
      <div className="pageContent">
        <PageContainer>
          <FilterDashboard
            loading={loading}
            onChangeParams={onChangeParams}
            open={openFilterPopup}
            setOpen={setOpenFilterPopup}
            initialParams={params}
          />
          <DashboardContainer>
            <DashboardHeader>
              <h3>Dashboard</h3>
              <IconButton onClick={() => setOpenFilterPopup(true)}>
                <IoFilter size={24} />
              </IconButton>
            </DashboardHeader>

            <ActiveFilters
              params={params}
              mapper={filterMapper}
              onRemoveFilter={handleRemoveFilter}
              onClearAll={handleClearAllFilters}
              title="Filtros aplicados"
              hideWhenEmpty={true}
            />
            <div className="dashboard-content">
              <CardsTop>
                {loading ? (
                  [...Array(4)].map((_, index) => (
                    <Skeleton
                      key={index}
                      variant="rectangular"
                      width="100%"
                      height={window.innerWidth <= 768 ? 80 : 100}
                      style={{ borderRadius: 12 }}
                    />
                  ))
                ) : (
                  <>
                    <DashboardCard
                      title="Pedidos"
                      value={`${data?.orderMetrics?.completedOrders || 0} / ${data?.orderMetrics?.totalOrders || 0}`}
                      icon={FaClipboardList}
                      iconColor="#BBDEFB"
                      iconTextColor="#1976D2"
                    />
                    <DashboardCard
                      title="Pedidos Cancelados"
                      value={data?.orderMetrics?.canceledOrders || 0}
                      icon={FaTimesCircle}
                      iconColor="#FFCDD2"
                      iconTextColor="#C62828"
                    />
                    <DashboardCard
                      title="Pedidos Pendentes"
                      value={data?.orderMetrics?.pendingOrders || 0}
                      icon={MdPendingActions}
                      iconColor="#FFF9C4"
                      iconTextColor="#F9A825"
                    />
                    <DashboardCard
                      title="Total de Quebras"
                      value={data?.totalComplaints || 0}
                      icon={MdOutlineBrokenImage}
                      iconColor="#FFE0B2"
                      iconTextColor="#EF6C00"
                    />
                  </>
                )}
              </CardsTop>

              <TablesAndCardsWrapper>
                <TablesContainer>
                  <TableHeader>
                    <HeaderBox>
                      {loading ? (
                        <Skeleton variant="text" width={270} height={40} />
                      ) : (
                        <Typography variant="h6">{params.groupBy === "driver" ? "Pontualidade dos Motoristas" : "Pontualidade dos Times"}</Typography>
                      )}
                      {loading ? (
                        <Skeleton variant="circular" width={24} height={24} />
                      ) : (
                        data.punctualityData && data.punctualityData.length > 0 && (
                          <SortButton
                            isAscending={sortPunctuality}
                            onChange={() => setSortPunctuality((prevState) => !prevState)}
                            disabled={loading}
                            title={
                              sortPunctuality
                                ? "Ordenar: Menor Pontualidade"
                                : "Ordenar: Maior Pontualidade"
                            }
                          />
                        )
                      )}
                    </HeaderBox>
                    {loading ? (
                      <Skeleton variant="rectangular" width={120} height={40} sx={{ borderRadius: 1 }} />
                    ) : (
                      data.punctualityData && data.punctualityData.length > 0 && (
                        <Button
                          variant="primary"
                          onClick={() => navigate(`/driver-punctuality?${searchParams.toString()}`)}
                        >
                          Ver detalhes
                        </Button>
                      )
                    )}
                  </TableHeader>
                  <StyledTableContainer className={sortedPunctualityData.length === 0 ? "centered" : ""}>
                    <PunctualityTable
                      data={sortedPunctualityData}
                      loading={loading}
                      searchParams={searchParams}
                      groupBy={params.groupBy}
                    />
                  </StyledTableContainer>

                  <TableHeader>
                    <HeaderBox>
                      {loading ? (
                        <Skeleton variant="text" width={220} height={40} />
                      ) : (
                        <Typography variant="h6">{params.groupBy === "driver" ? "Quebras dos Motoristas" : "Quebras dos Times"}</Typography>
                      )}
                      {loading ? (
                        <Skeleton variant="circular" width={24} height={24} />
                      ) : (
                        data.complaintsData && data.complaintsData.length > 0 && (
                          <SortButton
                            isAscending={sortComplaints}
                            onChange={() => setSortComplaints((prevState) => !prevState)}
                            disabled={loading}
                            title={
                              sortComplaints
                                ? "Ordenar: Maior Percentual de Quebras"
                                : "Ordenar: Menor Percentual de Quebras"
                            }
                          />
                        )
                      )}
                    </HeaderBox>
                    {loading ? (
                      <Skeleton variant="rectangular" width={120} height={40} sx={{ borderRadius: 1 }} />
                    ) : (
                      data.complaintsData && data.complaintsData.length > 0 && (
                        <Button
                          variant="primary"
                          onClick={() => navigate(`/driver-complaint?${searchParams.toString()}`)}
                        >
                          Ver detalhes
                        </Button>
                      )
                    )}
                  </TableHeader>
                  <StyledTableContainer className={sortedComplaints.length === 0 ? "centered" : ""}>
                    <ComplaintsTable
                      data={sortedComplaints}
                      loading={loading}
                      searchParams={searchParams}
                      groupBy={params.groupBy}
                    />
                  </StyledTableContainer>
                </TablesContainer>
                <CardsSide>
                  {loading ? (
                    [...Array(3)].map((_, index) => (
                      <Skeleton
                        key={index}
                        variant="rectangular"
                        width="100%"
                        height={window.innerWidth <= 768 ? 80 : 100}
                        style={{ borderRadius: 12 }}
                      />
                    ))
                  ) : (
                    <>
                      <>
                        <DashboardCard
                          title="Atrasos"
                          value={`${data?.punctualityMetrics?.totalDelays || 0} (${data?.punctualityMetrics?.totalDelayPercentage?.toFixed(2) || 0}%)`}
                          icon={FaExclamationTriangle}
                          iconColor="#FFCDD2"
                          iconTextColor="#C62828"
                        />
                        <DashboardCard
                          title="Pontualidade SLA"
                          value={`${data?.punctualityMetrics?.slaOnTime?.toFixed(2) || 0}%`}
                          icon={FaCheckCircle}
                          iconColor="#C8E6C9"
                          iconTextColor="#2E7D32"
                        />
                        <DashboardCard
                          title="Pontualidade SLA (60 min)"
                          value={`${data?.punctualityMetrics?.sla60min?.toFixed(2) || 0}%`}
                          icon={FaClock}
                          iconColor="#D1C4E9"
                          iconTextColor="#512DA8"
                        />
                      </>
                    </>
                  )}
                </CardsSide>
              </TablesAndCardsWrapper>
              <div className="spacer" style={{ height: 32 }} />
            </div>
          </DashboardContainer>
        </PageContainer>
      </div>
    </Container>
  );
};
